/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {gql} from 'graphql-request';
import isArray from 'lodash.isarray';
import isString from 'lodash.isstring';
import {isExplicitObject} from '@ion/core/src/utils/ObjectUtil';
import {contains} from '@ion/core/src/utils/StringUtil';
import * as logger from './GraphQLLogger';
import {
  Mutation,
  Query,
  SearchArgs
} from './types';

/**
 * Defines the query fields type which allows for specifying an array of fields
 * to be included in the response.
 *
 * If a field is a primitive type, the field name can be included as a string;
 * for example:
 *
 * ```typescript
 * {
 *   fields: ['username']
 * }
 * ```
 *
 * If a field is a complex type (i.e. an object, another type, etc.), the field
 * is to be included as a an object consisting of the field name as the key, and
 * the field values are an array:
 *
 * ```typescript
 * {
 *   fields: ['username', {
 *     extendedAttribute : ['value', 'applicationKey']
 *   }
 * }
 * ```
 * The generic types represent the following:
 *
 *   - T: The Type (e.g. Account)
 *   - F: A field of the Query type
 *   - P: A field of a Query type field type
 *   - K: A property of a Query type fields type
 */
type QueryFields<T> = Array<keyof T | {
  [F in keyof T]?: Array<keyof T[F]> | {
    [P in keyof T[F]]?: T[F][P] | Array<keyof T[F][P] | {
      [K in keyof T[F][P]]: Array<keyof T[F][P][K]>
    }>
  }
}>;

/**
 * Defines the parameters accepted by the `createQuery` helper. The generic types
 * represent the following:
 *
 *   - F: The fields array type
 *   - A: The argument types (optional)
 *   - S: The search type (optional)
 *
 * ```typescript
 *   // create a query params type which accepts `CurrentUser` fields
 *   export type CurrentUserParams = CreateQueryParams<CurrentUser>
 *   ...
 *
 *   // valid as all field and subfield values are valid `CurrentUser` properties ...
 *   const params: CurrentUserParams = {
 *     fields    : ['username', 'email', {
 *       extendedAttribute : ['value']
 *     }]
 *   };
 *   ...
 *
 *   // invalid (TS Error) as the provided fields and subfields are not valid
 *   // `CurrentUser` properties (i.e. 'usernames' should be 'username')...
 *   const params: CurrentUserParams = {
 *     fields    : ['usernames', 'emailAddress', {
 *       extendedAttribute : ['value']
 *     }]
 *   };
 * ```
 *
 * @see     {@link QueryFields}
 * @param   fields The top-level fields which are to be included in the response.
 *          Implementations are to override this type based on their requirements.
 * @param   search (optional) Search filter parameters to be included in the
 *          request.
 * @param   args (optional) An arbitrary object of arguments to be passed to the
 *          query. Implementations are to override this type based on the concrete
 *          implementation.
 */
export type CreateQueryParams<F = any, A = any, S = SearchArgs> = {
  fields: QueryFields<F>;
  args?: A;
  search?: S;
};

/**
 * Defines the parameters accepted by the `createMutation` helper. This type is
 * defined for semantics, and simply references the `CreateQueryParams` type.
 *
 * @see     {@link QueryFields}
 * @see     {@link CreateQueryParams}
 * @param   fields The top-level fields which are to be included in the response.
 * @param   args An object of key-value pairs to be passed to the request.
 */
export type CreateMutationParams = CreateQueryParams

/**
 * Defines the parameters accepted by the `createCombinedQuery` helper. This type
 * takes the form of an object whereby each key is a property name from the `Query`
 * type, and each value is a `CreateQueryParams`
 *
 * @param   An object whereby each key is a property name from the `Query` type,
 *          and each value is a `CreateQueryParams`
 */
export type CreateCombinedQueryParams = {
  [T in keyof Partial<Query>]: {
    fields: QueryFields<Query[T]>;
    args?: {
      [K in keyof Partial<Query[T]>]: any;
    };
    search?: SearchArgs;
  };
};

/**
 * Manages the creation of a GraphQL document based on a given `Query` and provided
 * arguments.
 *
 * ```typescript
 *   const query = createQuery('CurrentUser', {
 *     fields    : ['id', 'username', {
 *       extendedAttribute : ['value']
 *     }]
 *   });
 *   console.log(query);
 *   // {
 *   //   CurrentUser {
 *   //     id
 *   //     username,
 *   //     extendedAttribute {
 *   //       value
 *   //     }
 *   //   }
 *   // }
 * ```
 *
 * @see     {@link Query}
 * @see     {@link CreateQueryParams}
 * @param   query The type of `Query` that is to be made.
 * @param   params.fields The top-level fields which are to be included in the response.
 * @param   params.subfields (optional) The subfields of the top-level fields which are
 *          to be returned in the response.
 * @param   params.search (optional) Search filter parameters to be included in the
 *          request.
 * @returns A graphql document string based on the provide query arguments.
 */
export const createQuery = (query: keyof Query, params: CreateQueryParams, generator?: Function): string => {
  const api = generator || generateQuery;
  const document = compress(gql`${api(query, params, false)}`);
  logger.request(document);
  return document;
};

/**
 * Manages the creation of a GraphQL document based on a given `Mutation` and provided
 * arguments.
 *
 * ```typescript
 *   const mutation = createMutation('Login', {
 *     fields : ['authToken', {
 *       user : ['id', 'username']
 *     }]
 *   });
 *   console.log(mutation);
 *   // {
 *   //   Login {
 *   //     authToken
 *   //     user {
 *   //       id
 *   //       username
 *   //     }
 *   //   }
 *   // }
 * ```
 *
 * @see     {@link Mutation}
 * @see     {@link CreateMutationParams}
 * @param   query
 * @param   params.args
 * @param   params.fields
 * @param   params.subfields
 * @returns A graphql document string based on the provide mutation arguments.
 */
export const createMutation = (mutation: keyof Mutation, {
  args, fields
}: CreateMutationParams): string => {
  const newline  = '\n';
  const document = compress(gql`
    mutation {
      ${mutation}(${createParameters(args)}) {
        ${resolveQuery(fields as any).join(newline)}
      }
    }
  `);
  logger.request(document);
  return document;
};

export const normalize = (fields, newline: string) => (
  resolveQuery(fields as unknown as any).join(newline)
);

/**
 * Manages the creation of a combined GraphQL document based on the given `Query`
 * keys and their provided arguments.
 *
 * ```typescript
 *   const query = createCombinedQuery({
 *     CurrentUser: {
 *       fields : ['id', 'username', {
 *         extendedAttribute : ['value']
 *       }]
 *     },
 *     AccountQuery: {
 *       args   : {
 *          id: 123
 *       },
 *       fields : ['id', 'name']
 *     }
 *   });
 *   console.log(query);
 *   // {
 *   //   CurrentUser {
 *   //     id
 *   //     username,
 *   //     extendedAttribute {
 *   //       value
 *   //     }
 *   //   }
 *   //   AccountQuery(id: 123) {
 *   //     id
 *   //     name
 *   //   }
 *   // }
 * ```
 *
 * @see     {@link Query}
 * @see     {@link CreateQueryParams}
 * @see     {@link CreateCombinedQueryParams}
 * @param   queries An object whereby each key is a property name from the `Query`
 *          type, and each value is a `CreateQueryParams` object.
 * @returns A graphql document string based on the provide query arguments.
 */
export const createCombinedQuery = (queries: CreateCombinedQueryParams): string => {
  let document = '';
  Object.entries(queries).forEach(([query, params]) => {
    document = `${document} ${generateQuery(query as keyof Query, params as any, true)}`;
  });
  document = compress(gql`{${document}}`);
  logger.request(document);
  return document;
};

/**
 * @private
 *
 * Manages the creation of a GraphQL document based on a given `Query` and provided
 * arguments. This function provides an abstraction for generating queries for single
 * or combined queries.
 *
 * ```typescript
 *   // generates a stand-along query to be used as a single query ...
 *   const query = generateQuery('CurrentUser', {
 *     fields    : ['id', 'username', {
 *       extendedAttribute : ['value']
 *     }]
 *   });
 *   console.log(query);
 *   // {
 *   //   CurrentUser {
 *   //     id
 *   //     username,
 *   //     extendedAttribute {
 *   //       value
 *   //     }
 *   //   }
 *   // }
 *
 *   // generates a query to be used in a subsequent combined query, thus wrapping braces
 *   // will be excluded as they will be included in the final query of combined queries.
 *   const query = generateQuery('CurrentUser', {
 *     fields    : ['id', 'username', {
 *       extendedAttribute : ['value']
 *     }]
 *   }, false);
 *   console.log(query);
 *   // CurrentUser {
 *   //   id
 *   //   username,
 *   //   extendedAttribute {
 *   //     value
 *   //   }
 *   // }
 * ```
 *
 * @see     {@link Query}
 * @see     {@link CreateQueryParams}
 * @param   query The type of `Query` that is to be made.
 * @param   params.fields The top-level fields which are to be included in the response.
 * @param   params.subfields (optional) The subfields of the top-level fields which are
 *          to be returned in the response.
 * @param   params.search (optional) Search filter parameters to be included in the
 *          request.
 * @param   isCombined When `true`, indicates that the generated query will be part of
 *          a combined query, thus wrapping braces will be excluded. Defaults to `false`.
 * @returns A graphql document string based on the provide query arguments.
 */
const generateQuery = (query: keyof Query, {
  fields, search, args
}: CreateQueryParams, isCombined = false): string => {
  const newline = '\n';
  let document  = !isCombined ? '{' : '';
  if (search) {
    document += `
      ${query}(${createSearchFilters(search)}) {
        items {
          ${normalize(fields, newline)}
        }
        ${search.pageInfo ? resolveFields('pageInfo', search.pageInfo) : ''}
      }`;
  } else if (args) {
    document += `
      ${query}(${args && createParameters(args)}) {
        ${normalize(fields, newline)}
      }`;
  } else {
    document += `
      ${query} {
        ${normalize(fields, newline)}
      }`;
  }
  document += !isCombined ? '}' : '';
  return document;
};

/**
 * Given a collection of fields and a mapping of subfields for those fields,
 * returns an array of fields strings suitable for use within a GraphQL
 * document.
 *
 * ```typescript
 *   const results = resolveQuery(['name', 'roles'], {
 *     roles: ['id, 'name']
 *   });
 *   results.length // 2
 *   results[0]     // 'name'
 *   results[1]     // 'roles {id, name}'
 * ```
 *
 * @param   The fields which are to be validated, the fields can be top-level
 *          fields (such as 'name'), as well as nested fields, such as 'roles',
 *          in which case, the 'roles' field will be converted to an object
 *          shape and include all of the subfields provided for the 'roles'
 *          field via the `subfields` mapping.
 * @returns An array of field and subfield strings.
 */
const resolveQuery = (fields: string[]) => (
  fields?.map(field => {
    if (isExplicitObject(field)) {
      const [property, values] = Object.entries(field)[0];
      return resolveFields(property, values);
    }
    return field;
  }) || []
);

/**
 * Provides a utility which resolves a given top-level field's subfields and returns
 * the string.
 *
 * ```typescript
 *   const results = resolveFields('roles', ['name', 'id']);
 *   // 'roles {
 *   //    name
 *   //    id
 *   // }'
 * ```
 *
 * @param   field The field for which subfields are to be returned.
 * @param   values The fields subfields which are to be added to the field
 * @returns A string containing a field and it's subfields.
 */
const resolveFields = (field, values) => {
  if (!Array.isArray(values)) {
    if (isExplicitObject(values)) {
      let result = '';
      Object.entries(values).forEach(([prop, val]) => {
        if (isExplicitObject(val)) {
          const items = Object.entries(val).map(([e, v]) => `${e}: ${v}`).join(', ');
          if (prop !== 'paging') {
            result += `\n${field} {\n${prop} { ${items} }\n}`;
          } else {
            field = `${field} (${items})`;
          }
        } else if (Array.isArray(val)) {
          const included = contains(field, 'first') && contains(field, 'offset') ? 'count' : '';
          result += `${field} {\n ${included}\n${resolveFields(prop, val)}\n}`;
        } else {
          result += `${val}`;
        }
      });
      return result;
    }
    return '';
  } else {
    const items = values.map(val => {
      if (isExplicitObject(val)) {
        const [k, v] = Object.entries(val)[0];
        return `\n${k} {${v}}`;
      };
      return val;
    });
    return `${field} {\n${items?.join('\n')}\n}`;
  }
};

/**
 * Provides a utility which generates parameters to be included within a GraphQL
 * query string document.
 *
 * @param   args An object of key-value pairs from which the parameter string is to
 *          be generated..
 * @returns a parameter string containing the provided key-value pairs.
 */
const createParameters = (args: Object, isNumberArray: boolean = false): string => {
  let params = '';
  Object.entries(args).forEach(([prop, value]) => {
    params = `${params ? `${params},` : ''}`;
    let d = '';
    if (isArray(value)) {
      value?.forEach(id => {
        d = d+`${isNumberArray ? id : `"${id}"`},`;
      });
    }
    params = `${params} ${prop}: ${isArray(value) ? `[${d}]` : isString(value) ? `"${value}"` : value}`;
  });
  return params;
};

/**
 * Provides a utility which generates a search filter GraphQL query string document.
 *
 * @param   search the Search filters and parameter to be included in the query.
 * @returns a search filter query document string containing the provided filters
 *          and page inclusions. Note, if the `SearchArgs` `page` and `perPage`
 *          parameters are not included, they will default to 1, and 10, respectfully.
 */
export const createSearchFilters = (args: SearchArgs,
  isCommaSeparated: boolean = false, isNumberArray: boolean = false): string => {
  const {filter, page = 1, perPage = 10} = args;
  const filters = createParameters(filter, isNumberArray);
  return isCommaSeparated ? `${filters}` : `filter: {${filters}}, page: ${page}, perPage: ${perPage}`;
};

/**
 * Provides a utility which removes all newlines tokens and whitespace from a
 * query, effectively compressing the query document.
 *
 * ```typescript
 *  const compressed = compress(`
 *    query {
 *      user(id: 1) {
 *        id
 *        name
 *        email
 *      }
 *    }
 *  `);
 *  // 'query { user(id: 1) { id name email } }'
 * ```
 * @param   The query document which is to be compressed.
 * @returns The given query with all whitespace and newlines removed.
 */
export const compress = (query: string): string => (
  query.replace(/\s+/g, ' ').trim()
);
